import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, MapPin } from 'lucide-react';
import { CourierManagerService } from '../../services/courierManagerService';

const CourierManagerDashboard = () => {
  const navigate = useNavigate();

  const { data: shipmentsData, isLoading } = useQuery({
    queryKey: ['courierManagerShipments'],
    queryFn: () => CourierManagerService.getShipments(),
  });

  const shipments = shipmentsData?.data || [];

  const stats = {
    activeShipments: shipments.filter(s => s.status !== 'delivered' && s.status !== 'cancelled').length,
    inTransit: shipments.filter(s => s.status === 'in_transit' || s.status === 'in_transit').length,
    delivered: shipments.filter(s => s.status === 'delivered').length,
    pendingPickup: shipments.filter(s => s.status === 'pending' || s.status === 'pending_pickup').length,
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl p-6 text-white">
          <h1 className="text-3xl font-bold mb-2">Courier Manager Dashboard</h1>
          <p className="text-white/80">Manage courier operations and shipments</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">Courier Manager Dashboard</h1>
        <p className="text-white/80">Manage courier operations and shipments</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Active Shipments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeShipments}</p>
            </div>
            <Package size={24} className="text-amber-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">In Transit</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inTransit}</p>
            </div>
            <Truck size={24} className="text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Delivered</p>
              <p className="text-2xl font-bold text-gray-900">{stats.delivered}</p>
            </div>
            <MapPin size={24} className="text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending Pickup</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingPickup}</p>
            </div>
            <Package size={24} className="text-yellow-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button 
            onClick={() => navigate('/courier_manager/shipment-tracking')}
            className="px-4 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
          >
            Track Shipments
          </button>
          <button 
            onClick={() => navigate('/courier_manager/orders')}
            className="px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Manage Orders
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourierManagerDashboard;

